import { confirmInGrain } from "../utils/grainDialogs.js";
import React from "react";
import { ArrowRightLeft, CircleCheck, Lock, Pause, RotateCcw, ShieldCheck, Smartphone } from "lucide-react";
import { isFrozenDispatcherStatusCode } from "../utils/dispatcherFreezeStatus.js";
import { ActionButton, HoverCard, StatusBadge, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "./ResultsPrimitives.jsx";
import { getFleetProductTone } from "../utils/cropColors.js";
import { isNestedInteractiveEvent } from "../utils/interactionGuards.js";
import {
  buildVisibleProtectionStatus,
  getProtectionAccentColor,
  PROTECTION_VARIANT_REORDERED,
  resolveProtectionVariant,
} from "../utils/protectionVisualState.js";

const STEP_GRAIN_CODE_MAX_LENGTH = 22;
function buildOperationalStepMetaText(step, displayGrainCode = "") {
  const orderPart = step.orderId || "Ordre -";
  const quantityPart = typeof step.quantityLabel === "string" && step.quantityLabel.trim()
    ? step.quantityLabel.trim()
    : "Tonnage indisponible";
  return [orderPart, quantityPart, displayGrainCode].filter(Boolean).join(" · ");
}

function formatStepGrainCode(grainCode, maxLength = STEP_GRAIN_CODE_MAX_LENGTH) {
  if (typeof grainCode !== "string") {
    return "";
  }

  const normalizedGrainCode = grainCode.trim();
  if (!normalizedGrainCode || normalizedGrainCode.length <= maxLength) {
    return normalizedGrainCode;
  }

  return `${normalizedGrainCode.slice(0, Math.max(maxLength - 3, 1)).trimEnd()}...`;
}

function formatStepClientTitle(title) {
  if (typeof title !== "string") {
    return "";
  }

  const normalizedTitle = title.trim();
  if (!normalizedTitle.includes("(")) {
    return normalizedTitle;
  }

  const compactTitle = normalizedTitle.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s{2,}/g, " ").trim();
  return compactTitle || normalizedTitle;
}

function ManualStatusBadge({ manualStatus }) {
  if (!manualStatus) {
    return null;
  }

  if (manualStatus.code === "protected") {
    const protectionVariant = resolveProtectionVariant(manualStatus);
    return (
      <span className="inline-flex h-5 min-h-5 items-center justify-center">
        <span className="sr-only">{manualStatus.label}</span>
        <ShieldCheck
          aria-hidden="true"
          className={joinClasses([
            "h-5 w-5 stroke-[2.05]",
            protectionVariant === PROTECTION_VARIANT_REORDERED
              ? "text-[rgb(109,40,217)]"
              : "text-[var(--grain-badge-text)]",
          ])}
        />
      </span>
    );
  }

  if (manualStatus.code === "reassigned") {
    // Même repère que le Gantt : carré bleu plein, icône blanche.
    return (
      <span
        className="inline-flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-[4px] border border-white bg-[color-mix(in_srgb,rgb(29,78,216)_92%,white)] text-white"
        data-assignment-step-manual-status={manualStatus.code}
      >
        <ArrowRightLeft aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />
        <span className="sr-only">{manualStatus.label}</span>
      </span>
    );
  }

  const badgeClassName = `min-h-[1.45rem] gap-1.25 px-1.75 py-[0.18rem] text-[0.63rem] font-semibold leading-none tracking-[0.03em] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;

  return (
    <StatusBadge tone={manualStatus.tone} className={badgeClassName}>
      {renderStatusIcon(manualStatus.icon)}
      <span>{manualStatus.label}</span>
    </StatusBadge>
  );
}

function DispatcherStatusBadge({ dispatcherStatus }) {
  if (!dispatcherStatus) {
    return null;
  }

  return (
    <StatusBadge tone={dispatcherStatus.tone} className={`min-h-[1.45rem] gap-1.25 px-1.75 py-[0.18rem] text-[0.63rem] font-semibold leading-none tracking-[0.03em] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`}>
      {renderStatusIcon(dispatcherStatus.icon)}
      <span>{dispatcherStatus.label}</span>
    </StatusBadge>
  );
}

function formatTimeWindowRecordedAt(recordedAt) {
  if (typeof recordedAt !== "string") {
    return "";
  }

  const normalizedRecordedAt = recordedAt.trim();
  if (!normalizedRecordedAt) {
    return "";
  }

  const parsedDate = new Date(normalizedRecordedAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedRecordedAt;
  }

  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsedDate);
}

function TimeWindowOverrideBadge({ timeWindowOverride }) {
  if (!timeWindowOverride) {
    return null;
  }

  return (
    <span className={joinClasses([
      "inline-flex min-h-[1.45rem] items-center border border-border-warning-subtle bg-[rgba(255,244,226,0.96)] px-1.75 py-[0.18rem] text-[0.63rem] font-semibold leading-none tracking-[0.03em] text-status-warning-ink",
      DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
    ])}
    data-route-pill="true"
    >
      {timeWindowOverride.label}
    </span>
  );
}

function TimeWindowOverrideHoverCardContent({ timeWindowOverride }) {
  if (!timeWindowOverride) {
    return null;
  }

  const recordedAtLabel = formatTimeWindowRecordedAt(timeWindowOverride.recordedAt);

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">{timeWindowOverride.label}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">{timeWindowOverride.description}</div>
      <div className="grid gap-1 rounded-[14px] border border-border-warning-subtle bg-[rgba(255,244,226,0.96)] px-3 py-2 text-[11px] leading-[1.45] text-content-copy">
        <div className="grid grid-cols-[78px_minmax(0,1fr)] gap-2">
          <span className="font-semibold text-content-ink">Mode</span>
          <span>{timeWindowOverride.modeLabel}</span>
        </div>
        {timeWindowOverride.reasonLabel ? (
          <div className="grid grid-cols-[78px_minmax(0,1fr)] gap-2">
            <span className="font-semibold text-content-ink">Diagnostic</span>
            <span>{timeWindowOverride.reasonLabel}</span>
          </div>
        ) : null}
        {timeWindowOverride.reason ? (
          <div className="grid grid-cols-[78px_minmax(0,1fr)] gap-2">
            <span className="font-semibold text-content-ink">Motif</span>
            <span>{timeWindowOverride.reason}</span>
          </div>
        ) : null}
        {timeWindowOverride.code === "soft" ? (
          <div className="grid grid-cols-[78px_minmax(0,1fr)] gap-2">
            <span className="font-semibold text-content-ink">Validation</span>
            <span>{timeWindowOverride.confirmed ? "Confirmation enregistrée" : "Confirmation requise"}</span>
          </div>
        ) : null}
        {recordedAtLabel ? (
          <div className="grid grid-cols-[78px_minmax(0,1fr)] gap-2">
            <span className="font-semibold text-content-ink">Enregistré</span>
            <span>{recordedAtLabel}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StepHoverCardContent({ step }) {
  const accentColor = step.isLoadingStep ? "var(--dispatch-segment-loading-accent, var(--route-loading))" : "var(--dispatch-segment-delivery-accent, var(--route-delivery))";
  const orderDetails = step.orderDetails && typeof step.orderDetails === "object" ? step.orderDetails : null;
  const timeWindowOverride = step.timeWindowOverride && typeof step.timeWindowOverride === "object"
    ? step.timeWindowOverride
    : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <div className="flex items-start gap-2">
          <div className="flex flex-col items-center">
            <div
              className="mt-0.75 h-2 w-2 shrink-0 rounded-none"
              style={{ background: accentColor }}
            />
            <div className="h-5 border-l border-dashed border-[rgba(0,0,0,0.20)]" />
          </div>
          <div className="pb-1.5">
            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">
              {step.isLoadingStep ? "Chargement" : "Livraison"}
            </div>
            <div className="text-[12px] font-semibold text-content-ink">{step.title}</div>
            <div className="mt-0.5 text-[11px] leading-[1.4] text-content-muted">{step.addressLabel}</div>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <div
            className="mt-0.75 h-2 w-2 shrink-0 rounded-none border-2 border-solid bg-white"
            style={{ borderColor: accentColor }}
          />
          <div className="text-[11px] text-content-muted">{step.metaLabel}</div>
        </div>
      </div>

      {step.summaryLabel ? (
        <div className="border-t border-[rgba(0,0,0,0.08)] pt-2">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">
            Compartiments
          </div>
          <div className="text-[11px] font-medium text-content-ink">{step.summaryLabel}</div>
        </div>
      ) : null}

      {step.companionStep ? (
        <div className="border-t border-[rgba(0,0,0,0.08)] pt-2">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">
            Étape associée
          </div>
          <div className="grid gap-0.5 rounded-[14px] border border-black/6 bg-slate-50/80 px-3 py-2">
            <div className="text-[11px] font-semibold text-content-ink">
              {step.companionStep.sequenceLabel} · {step.companionStep.typeLabel}
            </div>
            <div className="text-[11px] font-medium text-content-copy">{step.companionStep.title}</div>
            <div className="text-[10px] leading-[1.45] text-content-muted">{step.companionStep.addressLabel}</div>
          </div>
        </div>
      ) : null}

      {orderDetails ? (
        <div className="border-t border-[rgba(0,0,0,0.08)] pt-2">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">
            Commande complète
          </div>
          <div className="grid gap-1.5">
            <div className="grid gap-1 rounded-[14px] border border-black/6 bg-white/88 px-3 py-2">
              <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                <span className="font-semibold text-content-ink">Commande</span>
                <span className="text-content-copy">{orderDetails.orderId || "-"}</span>
              </div>
              {orderDetails.grainCode ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Code produit</span>
                  <span className="text-content-copy">{orderDetails.grainCode}</span>
                </div>
              ) : null}
              {orderDetails.grainType ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Produit</span>
                  <span className="text-content-copy">{orderDetails.grainType}</span>
                </div>
              ) : null}
              {orderDetails.quantityLabel ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Quantité</span>
                  <span className="text-content-copy">{orderDetails.quantityLabel}</span>
                </div>
              ) : null}
              {orderDetails.loadingClientName || orderDetails.loadingAddress ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Chargement</span>
                  <span className="text-content-copy">
                    {orderDetails.loadingClientName || "Point de chargement"}
                    {orderDetails.loadingAddress ? <span className="block text-[10px] text-content-muted">{orderDetails.loadingAddress}</span> : null}
                  </span>
                </div>
              ) : null}
              {orderDetails.deliveryClientName || orderDetails.deliveryAddress ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Déchargement</span>
                  <span className="text-content-copy">
                    {orderDetails.deliveryClientName || "Point de livraison"}
                    {orderDetails.deliveryAddress ? <span className="block text-[10px] text-content-muted">{orderDetails.deliveryAddress}</span> : null}
                  </span>
                </div>
              ) : null}
              {orderDetails.deliveryWindow ? (
                <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[11px] leading-[1.45]">
                  <span className="font-semibold text-content-ink">Fenêtre</span>
                  <span className="text-content-copy">{orderDetails.deliveryWindow}</span>
                </div>
              ) : null}
            </div>

            {orderDetails.planningNote ? (
              <div className="rounded-[14px] border border-black/6 bg-slate-50/80 px-3 py-2 text-[10px] leading-normal text-content-muted">
                {orderDetails.planningNote}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {timeWindowOverride ? (
        <div className="border-t border-[rgba(0,0,0,0.08)] pt-2">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">
            Fenêtre horaire
          </div>
          <div className="grid gap-1.5 rounded-[14px] border border-border-warning-subtle bg-[rgba(255,244,226,0.96)] px-3 py-2">
            <div className="text-[11px] font-semibold text-content-ink">{timeWindowOverride.modeLabel}</div>
            <div className="text-[11px] leading-[1.45] text-content-copy">{timeWindowOverride.description}</div>
            {timeWindowOverride.reasonLabel ? (
              <div className="text-[10px] leading-[1.45] text-content-muted">
                <span className="font-semibold text-content-ink">Diagnostic</span>
                {" · "}
                {timeWindowOverride.reasonLabel}
              </div>
            ) : null}
            {timeWindowOverride.reason ? (
              <div className="text-[10px] leading-[1.45] text-content-muted">
                <span className="font-semibold text-content-ink">Motif</span>
                {" · "}
                {timeWindowOverride.reason}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ConfirmButtonHoverCardContent({ confirmButton }) {
  const title = typeof confirmButton?.title === "string" ? confirmButton.title.trim() : "";
  const ariaLabel = typeof confirmButton?.ariaLabel === "string" ? confirmButton.ariaLabel.trim() : "";
  const helperLabel = ariaLabel && ariaLabel !== title ? ariaLabel : "";

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">
        {title || ariaLabel || "Protection"}
      </div>
      {helperLabel ? (
        <div className="text-[11px] leading-[1.45] text-content-muted">{helperLabel}</div>
      ) : null}
    </div>
  );
}

function ManualStatusHoverCardContent({ manualStatus, actionNoteOverride = null }) {
  if (!manualStatus) {
    return null;
  }

  const actionNote = actionNoteOverride || (manualStatus.code === "reassigned"
    ? "Vous pouvez encore réaffecter cette commande vers un autre camion compatible. Utilisez Libérer seulement pour la rendre à nouveau optimisable globalement."
    : manualStatus.code === "protected"
      ? "Retirez d'abord la protection via l'action de protection si vous voulez autoriser une nouvelle réaffectation ou une optimisation globale."
      : "La commande restera hors flotte active jusqu'à une remise en optimisation explicite.");

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">{manualStatus.label}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">{manualStatus.description}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">{actionNote}</div>
    </div>
  );
}

function DispatcherStatusHoverCardContent({ dispatcherStatus }) {
  if (!dispatcherStatus) {
    return null;
  }

  const actionNote = dispatcherStatus.code === "frozen"
    ? "Ses autres actions sont masquées tant qu'elle reste gelée ; « Optimiser » ne la déplace pas."
    : dispatcherStatus.code === "frozen_prefix"
      ? "Cette commande hérite du gel d'une commande située plus loin dans la tournée. Elle reste jaune et non réoptimisable tant que le gel explicite en aval n'est pas retiré."
    : dispatcherStatus.code === "frozen_shared_loading"
      ? "Cette commande reste à bord après une livraison gelée du même chargement. Elle affiche un cadenas et n'est plus déplaçable, mais le chauffeur peut encore la confirmer."
    : "Commande livrée : aucune action disponible.";

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">{dispatcherStatus.label}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">{dispatcherStatus.description}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">{actionNote}</div>
    </div>
  );
}

function DispatcherActionHoverCardContent({ dispatcherActionButton, dispatcherStatus }) {
  if (!dispatcherActionButton) {
    return null;
  }

  const isUnfreeze = dispatcherActionButton.mode === "unfreeze";

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">{dispatcherActionButton.title}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">
        {isUnfreeze
          ? "Dégèle la commande ; ses autres actions redeviennent disponibles."
          : "Gèle la commande et celles chargées au même endroit sur ce camion."}
      </div>
      <div className="text-[11px] leading-[1.45] text-content-muted">
        {dispatcherStatus?.code === "frozen"
          ? "La commande restera figée tant que vous ne la dégelez pas explicitement."
          : "Une fois gelée, seule l'action Dégeler restera visible sur cette commande."}
      </div>
    </div>
  );
}

function ReassignButtonHoverCardContent({ reassignButton }) {
  const disabledLabel = reassignButton?.disabled
    ? "Action temporairement indisponible pendant une mise à jour en cours."
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-content-muted">Objectif</div>
        <div className="text-[11px] leading-normal text-content-copy">
          Déplace toute la commande vers un autre camion. La liste montre les camions possibles ;
          « Indisponible » donne la raison.
        </div>
      </div>

      <div className="text-[11px] leading-normal text-content-copy">
        Avec « Protéger après réaffectation » (menu « Plus »), la commande devient « Réaffectée » :
        « Optimiser » la laisse sur ce camion.
      </div>

      <div className="rounded-[14px] border border-border-warning-strong bg-[rgba(255,244,226,0.96)] px-3 py-2">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-[#9a6700]">Important</div>
        <div className="text-[11px] leading-normal text-content-copy">
          Si d'autres commandes devaient bouger, le déplacement est refusé et rien ne change.
        </div>
      </div>

      {disabledLabel ? (
        <div className="text-[11px] leading-[1.45] text-content-muted">{disabledLabel}</div>
      ) : null}
    </div>
  );
}

function SuspendButtonHoverCardContent({ suspendButton }) {
  const title = typeof suspendButton?.title === "string" ? suspendButton.title.trim() : "";
  const ariaLabel = typeof suspendButton?.ariaLabel === "string" ? suspendButton.ariaLabel.trim() : "";
  const helperLabel = ariaLabel && ariaLabel !== title ? ariaLabel : "";
  const stateLabel = suspendButton?.disabled
    ? "Une mise à jour est déjà en cours pour cette livraison."
    : "Suspend toute la commande associée à cette étape, la retire des tournées actives et la place dans un état manuel explicite.";

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">
        {title || ariaLabel || "Suspendre"}
      </div>
      {helperLabel ? (
        <div className="text-[11px] leading-[1.45] text-content-muted">{helperLabel}</div>
      ) : null}
      <div className="text-[11px] leading-[1.45] text-content-muted">{stateLabel}</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">
        La commande reste suspendue jusqu'à sa réactivation explicite et n'est pas réintégrée silencieusement au prochain clic sur Optimiser.
      </div>
    </div>
  );
}

function ReleaseButtonHoverCardContent() {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">Libérer pour optimisation</div>
      <div className="text-[11px] leading-[1.45] text-content-muted">
        Retire la réaffectation manuelle active et rend de nouveau cette commande éligible au prochain clic sur Optimiser.
      </div>
      <div className="text-[11px] leading-[1.45] text-content-muted">
        La commande reste visible sur son camion actuel tant qu'une réoptimisation globale n'est pas relancée.
      </div>
    </div>
  );
}

function renderActionIcon(iconName) {
  switch (iconName) {
    case "check":
    case "shield":
      return <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2]" />;
    case "shuffle":
      return <ArrowRightLeft aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.15]" />;
    case "pause":
      return <Pause aria-hidden="true" className="h-3.25 w-3.25 stroke-[2.3]" />;
    case "rotate-ccw":
      return <RotateCcw aria-hidden="true" className="h-3.25 w-3.25 stroke-[2.2]" />;
    default:
      return null;
  }
}

function renderStatusIcon(iconName) {
  switch (iconName) {
    case "shuffle":
      return <ArrowRightLeft aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />;
    case "pause":
      return <Pause aria-hidden="true" className="h-3 w-3 stroke-[2.3]" />;
    case "smartphone":
      return <Smartphone aria-hidden="true" className="h-4.75 w-4.75 stroke-[2.2] fill-green-200" />;
    case "lock":
      return <Lock aria-hidden="true" className="h-3 w-3 stroke-[2.3]" />;
    // « Livrée » : coche, distincte du bouclier de « Protégée ».
    case "check":
      return <CircleCheck aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />;
    case "shield":
      return <ShieldCheck aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />;
    case "rotate-ccw":
      return <RotateCcw aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />;
    default:
      return null;
  }
}

export default function ClientSelectableItem({
  interactions = {},
  servicePercent = 0,
  startsProductRun = false,
  step,
}) {
  const [isActionHoverActive, setIsActionHoverActive] = React.useState(false);
  const displayGrainCode = formatStepGrainCode(step?.grainCode);
  const displayTitle = formatStepClientTitle(step?.title);
  const stepMetaText = buildOperationalStepMetaText(step, displayGrainCode);
  const dispatcherStatusCode = typeof step.dispatcherStatus?.code === "string" ? step.dispatcherStatus.code : "";
  const manualStatusCode = typeof step.manualStatus?.code === "string" ? step.manualStatus.code : "";
  const shouldHideDispatcherActions = isFrozenDispatcherStatusCode(dispatcherStatusCode)
    || dispatcherStatusCode === "delivered";
  // Sans le droit « Modifier les affectations dispatch », « Protéger » / « Libérer », « Libérer » (commande déplacée),
  // « Réaffecter » et « Suspendre » sont grisés, avec la bulle.
  const canWritePlanning = interactions.canMutatePlanning === true;
  const shouldShowProtectedMarker = Boolean(step.releaseButton)
    || (manualStatusCode === "protected" && (shouldHideDispatcherActions || !step.confirmButton?.isActive));
  const shouldRenderInlineDispatcherStatus = Boolean(step.dispatcherStatus)
    && !(step.isLoadingStep && dispatcherStatusCode === "frozen_shared_loading");
  const shouldRenderInlineManualStatus = step.manualStatus
    && !["reordered", "protected"].includes(manualStatusCode)
    && !(dispatcherStatusCode === "frozen_shared_loading" && manualStatusCode === "reassigned");
  const shouldRenderTimeWindowOverride = Boolean(step.timeWindowOverride);
  const protectedBadgeStatus = buildVisibleProtectionStatus(step.manualStatus, {
    hasReleaseButton: Boolean(step.releaseButton),
    dispatcherStatusCode,
  });
  const hasStepActions = Boolean(step.dispatcherActionButton)
    || (!shouldHideDispatcherActions && Boolean(step.releaseButton || step.confirmButton || step.reassignButton || step.suspendButton));
  const productRunAccentColor = startsProductRun && step.grainType ? getFleetProductTone(step.grainType).accentColor : "";
  const displaySequence = Number(step.displaySequence) > 0 ? step.displaySequence : "–";
  const baseCardClassName = step.isLoadingStep
    ? "border-[var(--dispatch-segment-loading-border)] bg-[linear-gradient(var(--dispatch-segment-loading-fill),var(--dispatch-segment-loading-fill))] text-[var(--dispatch-segment-loading-ink)]"
    : "border-[var(--dispatch-segment-delivery-border)] bg-[linear-gradient(var(--dispatch-segment-delivery-fill),var(--dispatch-segment-delivery-fill))] text-[var(--dispatch-segment-delivery-ink)]";
  const selectedCardClassName = step.isSelected
    ? step.isLoadingStep
      ? "ring-2 ring-[var(--dispatch-segment-loading-border)]"
      : "ring-2 ring-[var(--dispatch-segment-delivery-border)]"
    : "";
  // Partie foncée du Gantt : durée du chargement ou du déchargement dans l'étape.
  const serviceOverlayClassName = step.isLoadingStep
    ? "bg-[image:var(--dispatch-segment-loading-service-overlay)]"
    : "bg-[image:var(--dispatch-segment-delivery-service-overlay)]";
  const linkedSelectionHighlightClassName = step.isLinkedSelectionHighlighted
    ? "animate-[linked-step-emphasis_1.65s_ease-in-out_2]"
    : "";
  const blockedClassName = step.isBlocked && !step.isSelected && step.isLoadingStep
    ? "border-[rgba(28,34,39,0.18)] bg-[rgba(243,246,248,0.97)]"
    : "";
  const hoverClassName = step.clientId
    ? "cursor-pointer hover:border-[rgba(71,85,105,0.2)]"
    : "cursor-default";

  const handleStepSelection = (event = null) => {
    if (event) {
      if (isNestedInteractiveEvent(event)) {
        return;
      }

      event.stopPropagation();
    }

    if (!step.clientId || typeof interactions.onSelectClient !== "function") {
      return;
    }

    interactions.onSelectClient({
      assignmentId: step.assignmentId,
      centerMap: true,
      clientId: step.clientId,
      tripId: step.tripId,
    });
  };

  const handleStepKeyDown = (event) => {
    if (isNestedInteractiveEvent(event)) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleStepSelection();
  };

  const stopStepEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const syncAssignmentSelection = () => {
    if (!step.assignmentId || typeof interactions.onSelectAssignment !== "function") {
      return;
    }

    interactions.onSelectAssignment(step.assignmentId);
  };

  const deactivateActionHover = (relatedTarget = null) => {
    if (relatedTarget instanceof Node && relatedTarget.closest?.("[data-step-action-region='true']")) {
      return;
    }

    setIsActionHoverActive(false);
  };

  return (
    <HoverCard
      content={<StepHoverCardContent step={step} />}
      placement="right-start"
      delay={1000}
      maxWidth={360}
      accentColor={step.isLoadingStep ? "var(--dispatch-segment-loading-accent, var(--route-loading))" : "var(--dispatch-segment-delivery-accent, var(--route-delivery))"}
      disabled={isActionHoverActive}
    >
      <div
        className="relative min-w-0 w-full"
        onClick={(event) => {
          handleStepSelection(event);
        }}
        onKeyDown={handleStepKeyDown}
        onMouseEnter={() => {
          if (typeof interactions.onClientHoverStart === "function" && step.clientId) {
            interactions.onClientHoverStart(step.clientId);
          }
        }}
        onMouseLeave={() => {
          if (typeof interactions.onClientHoverEnd === "function") {
            interactions.onClientHoverEnd();
          }
        }}
        data-delivery-item="true"
        data-assignment-id={step.assignmentId}
        data-order-id={step.orderId}
        data-step-id={step.stepId}
        data-step-type={step.stepType}
        data-trip-id={step.tripId}
        role={step.clientId ? "button" : undefined}
        tabIndex={step.clientId ? 0 : undefined}
        {...(step.clientId
          ? {
              "data-client-selectable": "true",
              "data-client-id": step.clientId,
              "aria-pressed": step.isSelected ? "true" : "false",
            }
          : {})}
        {...(step.deliveryId ? { "data-delivery-id": step.deliveryId } : {})}
      >
        <article
          className={joinClasses([
            "group relative min-w-0 overflow-hidden rounded-[4px] border transition duration-150",
            baseCardClassName,
            selectedCardClassName,
            linkedSelectionHighlightClassName,
            blockedClassName,
            hoverClassName,
          ])}
          style={productRunAccentColor ? { boxShadow: `inset 0 2px 0 ${productRunAccentColor}` } : undefined}
        >
          {servicePercent > 0 ? (
            <span
              aria-hidden="true"
              className={joinClasses([
                "pointer-events-none absolute inset-y-0 left-0 border-r border-[color-mix(in_srgb,currentColor_18%,transparent)]",
                serviceOverlayClassName,
              ])}
              data-step-service-overlay="true"
              style={{ width: `${Math.min(servicePercent, 100)}%` }}
            />
          ) : null}

          <div className="relative grid grid-cols-[1rem_minmax(0,1fr)] gap-x-1 px-1 py-0.75">
            <span className="pt-px text-center text-[0.74rem] font-extrabold leading-[1.15] tabular-nums" title={step.sequenceLabel}>
              {displaySequence}
            </span>

            <div className="grid min-w-0 gap-0.5">
              <div className="flex min-w-0 items-start gap-1">
                <strong className="min-w-0 flex-1 font-display text-[0.8rem] font-semibold leading-[1.15] [overflow-wrap:anywhere]">
                  {displayTitle}
                </strong>

                {shouldRenderInlineManualStatus ? (
                  <HoverCard
                    title={`Statut manuel · ${step.manualStatus.label}`}
                    content={<ManualStatusHoverCardContent manualStatus={step.manualStatus} />}
                    placement="right-start"
                    delay={1000}
                    maxWidth={320}
                    accentColor={step.manualStatus.code === "protected" ? getProtectionAccentColor(step.manualStatus) : "var(--amber)"}
                  >
                    <span className="flex shrink-0">
                      <ManualStatusBadge manualStatus={step.manualStatus} />
                    </span>
                  </HoverCard>
                ) : null}

                {shouldShowProtectedMarker && protectedBadgeStatus ? (
                  <HoverCard
                    title={step.releaseButton ? "Commande protégée" : `Statut manuel · ${protectedBadgeStatus.label}`}
                    content={(
                      <ManualStatusHoverCardContent
                        manualStatus={protectedBadgeStatus}
                        actionNoteOverride={step.releaseButton ? "Utilisez Libérer pour rendre cette commande à nouveau optimisable. Une nouvelle réaffectation manuelle reste possible tant qu'un camion compatible est disponible." : null}
                      />
                    )}
                    placement="right-start"
                    delay={1000}
                    maxWidth={320}
                    accentColor={getProtectionAccentColor(protectedBadgeStatus)}
                  >
                    <span className="flex shrink-0 items-center justify-end">
                      <ManualStatusBadge manualStatus={protectedBadgeStatus} />
                    </span>
                  </HoverCard>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-wrap items-center gap-1">
                <p
                  className="min-w-0 text-[0.7rem] font-semibold leading-[1.15] text-current"
                  title={step.grainCode ? `Code produit : ${step.grainCode}` : undefined}
                >
                  {stepMetaText}
                </p>

                {shouldRenderInlineDispatcherStatus ? (
                  <HoverCard
                    title={`Statut dispatcher · ${step.dispatcherStatus.label}`}
                    content={<DispatcherStatusHoverCardContent dispatcherStatus={step.dispatcherStatus} />}
                    placement="right-start"
                    delay={1000}
                    maxWidth={320}
                    accentColor={dispatcherStatusCode === "delivered" ? "var(--color-accent)" : "var(--amber)"}
                  >
                    <span className="inline-flex">
                      <DispatcherStatusBadge dispatcherStatus={step.dispatcherStatus} />
                    </span>
                  </HoverCard>
                ) : null}

                {shouldRenderTimeWindowOverride ? (
                  <HoverCard
                    title={step.timeWindowOverride.label}
                    content={<TimeWindowOverrideHoverCardContent timeWindowOverride={step.timeWindowOverride} />}
                    placement="right-start"
                    delay={1000}
                    maxWidth={340}
                    accentColor="var(--amber)"
                  >
                    <span className="inline-flex">
                      <TimeWindowOverrideBadge timeWindowOverride={step.timeWindowOverride} />
                    </span>
                  </HoverCard>
                ) : null}
              </div>

              {hasStepActions ? (
                <div
                  className="flex min-w-0 items-center gap-0.5 whitespace-nowrap"
                  data-step-action-region="true"
                  onMouseEnter={() => {
                    setIsActionHoverActive(true);
                  }}
                  onMouseLeave={(event) => {
                    deactivateActionHover(event.relatedTarget);
                  }}
                  onFocusCapture={() => {
                    setIsActionHoverActive(true);
                  }}
                  onBlurCapture={(event) => {
                    deactivateActionHover(event.relatedTarget);
                  }}
                >
                  {step.dispatcherActionButton ? (
                    <HoverCard
                      title={step.dispatcherActionButton.title}
                      content={<DispatcherActionHoverCardContent dispatcherActionButton={step.dispatcherActionButton} dispatcherStatus={step.dispatcherStatus} />}
                      placement="right-start"
                      delay={1000}
                      maxWidth={320}
                      accentColor={step.dispatcherActionButton.mode === "unfreeze"
                        ? (step.dispatcherActionButton.gelOrigine === "portail" ? "var(--color-info)" : "var(--color-accent)")
                        : (step.dispatcherActionButton.gelOrigine === "portail" ? "var(--color-info)" : "var(--amber)")}
                    >
                      <ActionButton
                        size="sm"
                        tone={step.dispatcherActionButton.mode === "unfreeze"
                          ? (step.dispatcherActionButton.gelOrigine === "portail" ? "icon-info" : "icon-success")
                          : (step.dispatcherActionButton.gelOrigine === "portail" ? "icon-info" : "icon-suspend")}
                        className={`mr-0.5! min-h-4.5! px-1.25! py-[0.08rem]! text-[0.58rem]! font-semibold! leading-none! tracking-[0.03em]! ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`}
                        data-route-action="true"
                        aria-label={step.dispatcherActionButton.ariaLabel}
                        disabled={step.dispatcherActionButton.disabled}
                        onClick={async (event) => {
                          stopStepEvent(event);
                          syncAssignmentSelection();
                          if (step.dispatcherActionButton.mode === "unfreeze") {
                            if (step.dispatcherActionButton.requiresPortalUnfreezeConfirmation) {
                              if (!await confirmInGrain(
                                "Dégeler une commande confirmée par le portail ?\n\n" +
                                "La confirmation passera à « à revalider » et le chauffeur devra revalider cette livraison."
                              )) {
                                return;
                              }
                            }
                            interactions.onUnfreezeDelivery?.(step.dispatcherActionButton.deliveryId);
                            return;
                          }

                          interactions.onFreezeDelivery?.(step.dispatcherActionButton.deliveryId);
                        }}
                      >
                        <span className="inline-flex items-center gap-1 whitespace-nowrap leading-none">
                          {renderActionIcon(step.dispatcherActionButton.icon)}
                          <span>{step.dispatcherActionButton.label}</span>
                        </span>
                      </ActionButton>
                    </HoverCard>
                  ) : null}

                  {!shouldHideDispatcherActions && step.releaseButton ? (
                    <HoverCard
                      title={step.releaseButton.title}
                      content={<ReleaseButtonHoverCardContent />}
                      placement="right-start"
                      delay={1000}
                      maxWidth={320}
                      accentColor="rgba(208,139,62,0.58)"
                    >
                      <ActionButton
                        size="sm"
                        tone="icon"
                        className={`mr-0.5! min-h-4.5! px-1.25! py-[0.08rem]! text-[0.58rem]! font-semibold! leading-none! tracking-[0.03em]! border-border-warning-subtle! bg-amber-soft! text-status-warning-ink! shadow-none hover:border-border-warning-muted! hover:bg-[rgba(255,244,226,0.96)]! hover:text-status-warning-ink! hover:shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`}
                        data-route-action="true"
                        aria-label={step.releaseButton.ariaLabel}
                        disabled={step.releaseButton.disabled || !canWritePlanning}
                        title={canWritePlanning ? undefined : "Votre rôle n'a pas ce droit."}
                        onClick={(event) => {
                          stopStepEvent(event);
                          syncAssignmentSelection();
                          interactions.onReleaseDelivery?.(step.releaseButton.deliveryId);
                        }}
                      >
                        <span className="inline-flex items-center gap-1 whitespace-nowrap leading-none">
                          {renderStatusIcon(step.releaseButton.icon) || renderActionIcon(step.releaseButton.icon)}
                          <span>{step.releaseButton.label}</span>
                        </span>
                      </ActionButton>
                    </HoverCard>
                  ) : null}

                  <span aria-hidden="true" className="flex-1" />

                  {!shouldHideDispatcherActions && step.confirmButton ? (
                    <HoverCard
                      title="Protection"
                      content={<ConfirmButtonHoverCardContent confirmButton={step.confirmButton} />}
                      placement="right-start"
                      delay={1000}
                      maxWidth={260}
                      accentColor={step.confirmButton.isBlocked ? "rgba(20,184,166,0.82)" : "var(--color-accent)"}
                    >
                      <ActionButton
                        size="icon"
                        tone={step.confirmButton.isBlocked
                          ? "icon-success-blocked"
                          : (step.confirmButton.isActive ? "icon-success-active" : "icon-success")}
                        className="h-4.5! w-4.5!"
                        aria-label={step.confirmButton.ariaLabel}
                        aria-pressed={step.confirmButton.ariaPressed}
                        disabled={step.confirmButton.disabled || !canWritePlanning}
                        title={canWritePlanning ? undefined : "Votre rôle n'a pas ce droit."}
                        onClick={(event) => {
                          stopStepEvent(event);
                          syncAssignmentSelection();
                          if (step.confirmButton.type === "loading" && typeof interactions.onToggleLoadingConfirm === "function") {
                            interactions.onToggleLoadingConfirm({
                              isConfirmed: step.confirmButton.isActive,
                              loadingStepId: step.confirmButton.targetId,
                            });
                            return;
                          }

                          if (step.confirmButton.type === "delivery" && typeof interactions.onToggleDeliveryConfirm === "function") {
                            interactions.onToggleDeliveryConfirm({
                              deliveryId: step.confirmButton.targetId,
                              isConfirmed: step.confirmButton.isActive,
                            });
                          }
                        }}
                      >
                        {renderActionIcon(step.confirmButton.icon) || step.confirmButton.text}
                      </ActionButton>
                    </HoverCard>
                  ) : null}

                  {!shouldHideDispatcherActions && step.reassignButton ? (
                    <HoverCard
                      title={step.reassignButton.title || "Réaffectation de commande"}
                      content={<ReassignButtonHoverCardContent reassignButton={step.reassignButton} />}
                      placement="right-start"
                      delay={1000}
                      maxWidth={420}
                      accentColor="var(--amber)"
                    >
                      <ActionButton
                        size="icon"
                        tone={step.reassignButton.isActive ? "icon-reassign-active" : "icon-reassign"}
                        className="h-4.5! w-4.5!"
                        aria-expanded={step.reassignButton.ariaExpanded}
                        aria-label={step.reassignButton.ariaLabel}
                        data-delivery-reassign-toggle="true"
                        disabled={step.reassignButton.disabled || !canWritePlanning}
                        title={canWritePlanning ? undefined : "Votre rôle n'a pas ce droit."}
                        onClick={(event) => {
                          stopStepEvent(event);
                          syncAssignmentSelection();
                          interactions.onToggleReassignment?.({
                            anchorElement: event.currentTarget,
                            assignmentId: step.reassignButton.assignmentId,
                            stepId: step.reassignButton.stepId,
                            stepType: step.stepType,
                          });
                        }}
                      >
                        {renderActionIcon(step.reassignButton.icon) || step.reassignButton.text}
                      </ActionButton>
                    </HoverCard>
                  ) : null}

                  {!shouldHideDispatcherActions && step.suspendButton ? (
                    <HoverCard
                      title={step.suspendButton.title || "Suspendre"}
                      content={<SuspendButtonHoverCardContent suspendButton={step.suspendButton} />}
                      placement="right-start"
                      delay={1000}
                      maxWidth={320}
                      accentColor="var(--amber)"
                    >
                      <ActionButton
                        size="icon"
                        tone="icon-suspend"
                        className="h-4.5! w-4.5!"
                        aria-label={step.suspendButton.ariaLabel}
                        disabled={step.suspendButton.disabled || !canWritePlanning}
                        title={canWritePlanning ? undefined : "Votre rôle n'a pas ce droit."}
                        onClick={(event) => {
                          stopStepEvent(event);
                          syncAssignmentSelection();
                          interactions.onSuspendDelivery?.(step.suspendButton.deliveryId);
                        }}
                      >
                        {renderActionIcon(step.suspendButton.icon) || step.suspendButton.text}
                      </ActionButton>
                    </HoverCard>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </article>
      </div>
    </HoverCard>
  );
}
